import type { GameObj } from 'kaplay'
import type { KAPLAYCtx } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'

export function useFloor13(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('overlay', `sprites/overlay.png?${cacheStr}`, {
    sliceX: 4,
    sliceY: 2,
    anims: {
      question: {
        frames: [0],
      },
    },
  })
  k.loadSprite('floor13', `sprites/floors/floor-13.png?${cacheStr}`)
  k.loadSprite('floor13Hover', `sprites/floors/floor-13--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON('floor13Colliders', `colliders/floor-13.parsed.json?${cacheStr}`)

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function getQuestionObj() {
    return [
      k.sprite('overlay', { anim: 'question' }),
      k.area({ isSensor: true }),
      k.layer('background'),
      'sfx_emit_action',
      'floor_13_info',
      'info',
    ]
  }

  function setFloor13() {
    const floor = k.add()

    k.onLoad(() => {
      const collidersData = collidersJson.data

      floor.add([
        k.sprite('floor13', {
          width: getTiledSize(collidersData.width),
          height: getTiledSize(collidersData.height),
        }),
        k.layer('background'),
        'floor_13_background',
        'background',
      ])

      floor.add([
        k.sprite('floor13Hover', {
          width: getTiledSize(collidersData.width),
          height: getTiledSize(collidersData.height),
        }),
        k.layer('hover'),
        'floor_13_hover',
        'hover',
      ])

      collidersData.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        floor.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      floor.add([
        k.level(
          [
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                w                          N                     ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                       p                                                         ',
            '                    q                                                            ',
            '                                                     u           6               ',
            '                                                                                ',
            '                                                              s                  ',
            '                                                                                 ',
            '               y                                                                 ',
            '                                      v                                          ',
            '                                                                                 ',
            '               5                                                                 ',
            '     1                                                                           ',
            '                       x                                                         ',
            '                           #                                                     ',
            '                                                                                 ',
            '     2              $                                             7         $    ',
            '                                                                                 ',
            '        3 4                #                                                     ',
            '                                                                         &       ',
            '                                                                                 ',
            '                                                                                 ',
            '                           #                                                     ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                           #                                                     ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {

              // Elevators
              '#': () => [
                k.rect(getTiledSize(), getTiledSize(1.5), { fill: false }),
                k.pos(k.vec2(getTiledSize(0.5), 0)),
                k.area({ isSensor: true }),
                'floor_13_elevator',
                'elevator',
              ],
              '&': () => [
                k.rect(getTiledSize(2), getTiledSize(1), { fill: false }),
                k.pos(k.vec2(0, getTiledSize(0.5))),
                k.area({ isSensor: true }),
                'floor_13_elevator',
                'elevator',
              ],
              '$': () => [
                k.rect(getTiledSize(2), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_stairs',
                'stairs',
              ],

              // Doors
              '1': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_door_to_meeting_room_j',
                'floor_13_door',
                'door',
              ],
              '2': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_door_to_meeting_room_k',
                'floor_13_door',
                'door',
              ],
              '3': () => [
                k.rect(getTiledSize(), getTiledSize(0.5), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_door_to_meeting_room_l',
                'floor_13_door',
                'door',
              ],
              '4': () => [
                k.rect(getTiledSize(), getTiledSize(0.5), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_door_to_meeting_room_m',
                'floor_13_door',
                'door',
              ],
              '5': () => [
                k.rect(getTiledSize(), getTiledSize(0.5), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_door_to_wc',
                'floor_13_door',
                'door',
              ],
              '6': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_door_to_wc_b',
                'floor_13_door',
                'door',
              ],
              '7': () => [
                k.rect(getTiledSize(2), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_13_door_to_wc_handicap',
                'floor_13_door',
                'door',
              ],

              // Interactions
              // z: () => [
              //   ...getQuestionObj(),
              //   '',
              // ],
              y: () => [
                ...getQuestionObj(),
                'floor_13_info_storage',
              ],
              x: () => [
                ...getQuestionObj(),
                'floor_13_info_intro',
              ],
              // w: () => [
              //   ...getQuestionObj(),
              //   '',
              // ],
              v: () => [
                ...getQuestionObj(),
                'floor_13_info_restaurant',
              ],
              u: () => [
                ...getQuestionObj(),
                'floor_13_info_lunchbox',
              ],
              // t: () => [
              //   ...getQuestionObj(),
              //   '',
              // ],
              s: () => [
                ...getQuestionObj(),
                'floor_13_info_comms_room',
              ],
              // r: () => [
              //   ...getQuestionObj(),
              //   '',
              // ],
              q: () => [
                ...getQuestionObj(),
                'floor_13_info_first_aid',
              ],
              p: () => [
                ...getQuestionObj(),
                'floor_13_info_host_coffee_point',
              ],

              // Easter Egg
              N: () => [
                k.rect(getTiledSize(1.75), getTiledSize(2), { fill: false }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'floor_13_piano_easteregg',
                'easteregg',
              ],
            },
          },
        ),
        k.layer('objects'),
        'floor_13',
        'floor',
      ])

      // Info
      k.onCollide('floor_13_info', 'player', (info: GameObj) => {
        if (info.tags.includes('floor_13_info_intro'))
          showTextFrame(getLangString('floor_13_info_intro'))
        else if (info.tags.includes('floor_13_info_storage'))
          showTextFrame(getLangString('comms_room_info'))
        else if (info.tags.includes('floor_13_info_host_coffee_point'))
          showTextFrame(getLangString('host_coffee_point_info'))
        else if (info.tags.includes('floor_13_info_first_aid'))
          showTextFrame(getLangString('first_aid_info'))
        else if (info.tags.includes('floor_13_info_restaurant'))
          showTextFrame(getLangString('restaurant_info'))
        else if (info.tags.includes('floor_13_info_lunchbox'))
          showTextFrame(getLangString('lunchbox_info'))
        else if (info.tags.includes('floor_13_info_comms_room'))
          showTextFrame(getLangString('comms_room_info'))
      })
      k.onCollideEnd('player', 'info', () => {
        showTextFrame()
      })

    })

    return floor
  }

  return {
    setFloor13,
  }
}
